import { z } from "zod";
import { Cents, Id, IsoDate, IsoDateTime, Ok } from "./common.ts";
import { endpoint } from "./endpoint.ts";
import { LegalForm } from "./company.ts";
import { FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS, regimeOf } from "./accounting/common.ts";

/**
 * Core company-creation dossier, shared by the creation forms (boards 34–63)
 * and the tracking screens (64–101). Form-specific schemas and endpoints are
 * added below by the forms slice; tracking lives in creation-tracking.ts.
 */

/** The 8 tracking phases of a company (SASU/EURL) dossier — board 65 tracker. */
export const COMPANY_PHASES = [
  "Préparation",
  "Vérification Melrys",
  "Dépôt du capital",
  "Finalisation des documents",
  "Dépôt de l'annonce légale",
  "Dépôt INPI",
  "Vérification INPI",
  "Décision et espace après création",
] as const;

export const DossierStatus = z.enum([
  "draft", // form being filled in
  "submitted", // sent, payment pending or done; Melrys hasn't picked it up yet
  "in_progress", // being processed through the phases
  "registered", // company created (Kbis / SIRET issued)
  "cancelled",
  "rejected",
]);
export type DossierStatus = z.infer<typeof DossierStatus>;

export const Dossier = z.object({
  id: Id,
  companyId: Id,
  /** V1 forms: SASU, EURL, AE. SAS/SARL are "bientôt disponible" on the site. */
  legalForm: LegalForm,
  companyName: z.string().nullable(),
  status: DossierStatus,
  /** 1-based index into COMPANY_PHASES (AE dossiers use their own, shorter flow). */
  phase: z.number().int().min(1).max(8),
  /** Client added Melrys accounting while creating (boards 39/40). */
  withAccounting: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /** Human reference shown to the client, e.g. "AC_2026_014" (board 35). */
  reference: z.string().optional(),
  /** When the questionnaire was sent to Melrys (board 53 « Envoyer mon dossier »). */
  submittedAt: IsoDateTime.optional(),
});
export type Dossier = z.infer<typeof Dossier>;

// ———————————————————————————————————————————————————————————————
// Catalogue (boards 34–40)
// ———————————————————————————————————————————————————————————————

/** Forms open in self-service. SAS / SARL are « bientôt ». */
export const CreationLegalForm = z.enum(["SASU", "EURL", "AE"]);
export type CreationLegalForm = z.infer<typeof CreationLegalForm>;

/** A one-off creation fee: HT, VAT 20 % and TTC, all from the server. */
export const CreationFee = z.object({ htCents: Cents, vatCents: Cents, ttcCents: Cents });
export type CreationFee = z.infer<typeof CreationFee>;

/** « Création seule »: Melrys creation fee per form, HT, paid once (legal fees excluded). */
export const CREATION_FEE_CENTS: Record<CreationLegalForm, number> = { AE: 7900, SASU: 14900, EURL: 14900 };

/**
 * « Création si compta »: effective creation price when the client also takes Melrys accounting
 * (AE 49 €, SASU / EURL 99 € HT). The creation-only fee is still paid at creation; the difference comes off
 * the first accounting invoice (FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS).
 */
export const CREATION_BUNDLE_CENTS: Record<CreationLegalForm, number> = {
  AE: CREATION_FEE_CENTS.AE - FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS[regimeOf("AE")],
  SASU: CREATION_FEE_CENTS.SASU - FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS[regimeOf("SASU")],
  EURL: CREATION_FEE_CENTS.EURL - FIRST_ACCOUNTING_MONTH_DISCOUNT_CENTS[regimeOf("EURL")],
};

/** Creation bundled with accounting, for one form (boards 39/40). */
export const CreationBundlePrice = z.object({
  /** Effective creation price with accounting: 49 € (AE) / 99 € (SASU, EURL) HT. */
  effectiveHtCents: Cents,
  /** What is actually paid at creation: the creation-only fee (79 € / 149 € HT). */
  paidAtCreationHtCents: Cents,
  /** Deducted once from the first monthly accounting invoice: 30 € (AE) / 50 € (SASU, EURL) HT. */
  firstAccountingMonthDiscountCents: Cents,
});
export type CreationBundlePrice = z.infer<typeof CreationBundlePrice>;

/**
 * Creation price list (picker, board 35 forms, briefings, wizard tips, bundles): the Melrys fee per form
 * (paid once, legal fees excluded), the bundled price with accounting, and the indicative legal fees of a company.
 */
export const CreationPricing = z.object({
  fees: z.object({ AE: CreationFee, SASU: CreationFee, EURL: CreationFee }),
  /** « Création si compta » per form. */
  bundle: z.object({ AE: CreationBundlePrice, SASU: CreationBundlePrice, EURL: CreationBundlePrice }),
  /** Indicative legal fees of a company (legal notice at the journal's price + INPI), « ≈ 250 € HT ». */
  legalFeesEstimateCents: Cents,
  /** Indicative INPI / registry fees alone, « frais INPI (~66 €) ». */
  inpiFeesEstimateCents: Cents,
});
export type CreationPricing = z.infer<typeof CreationPricing>;
/** Minimum paid-in share of the capital at creation. */
export const MIN_PAID_IN_PCT: Record<"SASU" | "EURL", number> = { SASU: 50, EURL: 20 };

// ———————————————————————————————————————————————————————————————
// Questionnaire: steps and answer drafts (boards 41–63)
// ———————————————————————————————————————————————————————————————

export const COMPANY_STEPS = ["eligibilite", "societe", "personnes", "fiscal", "siege", "activites"] as const;
export const AE_STEPS = ["anteriorite", "identite", "situation", "cotisations", "adresse", "activite"] as const;
export const StepKey = z.enum([...COMPANY_STEPS, ...AE_STEPS]);
export type StepKey = z.infer<typeof StepKey>;
export const stepsFor = (form: CreationLegalForm): readonly StepKey[] => (form === "AE" ? AE_STEPS : COMPANY_STEPS);

/** null = not answered yet. */
const YesNo = z.boolean().nullable().optional();
const Text = z.string().max(15000).optional();
const Short = z.string().max(300).optional();
const DateOrEmpty = z.union([IsoDate, z.literal("")]).nullable().optional();

export const AddressDraft = z.object({
  street: Short,
  complement: Short,
  postcode: Short,
  city: Short,
  country: Short,
});
export type AddressDraft = z.infer<typeof AddressDraft>;

export const MaritalStatus = z.enum(["single", "divorced", "widowed", "pacs", "cohabiting", "married"]);

/** Identity sheet shared by the shareholder, a third-party president / manager and the DG / co-manager. */
export const PersonDraft = z.object({
  firstNames: z.array(z.string().max(100)).max(4).optional(),
  lastName: Short,
  usageName: Short,
  /** G1 (retour 3): only « Masculin » / « Féminin » are offered (the INPI knows nothing else); "X" (« Autre ») is still read from old drafts. */
  gender: z.enum(["M", "F", "X"]).nullable().optional(),
  /** C04 (retour 3): no longer asked. Old drafts with gender « Autre » keep the civilité they declared to the INPI. */
  inpiGender: z.enum(["M", "F"]).nullable().optional(),
  birthDay: z.number().int().min(1).max(31).nullable().optional(),
  birthMonth: z.number().int().min(1).max(12).nullable().optional(),
  birthYear: z.number().int().min(1900).max(2100).nullable().optional(),
  /** « Je n'ai pas de jour/mois de naissance ». */
  noBirthDayMonth: z.boolean().optional(),
  birthCity: Short,
  birthCountry: Short,
  nationality: Short,
  taxResidence: Short,
  maritalStatus: MaritalStatus.nullable().optional(),
  address: AddressDraft.optional(),
  phonePrefix: Short,
  /** National number, digits only. */
  phone: Short,
  email: Short,
  /** Minor who declares being emancipated (proof asked at the documents step). */
  emancipated: z.boolean().optional(),
});
export type PersonDraft = z.infer<typeof PersonDraft>;

export const EligibiliteDraft = z.object({
  agricultural: YesNo,
  /** Merger, split, transformation, takeover of a business… */
  origin: YesNo,
  cape: YesNo,
  mission: YesNo,
  ess: YesNo,
  presidentIsCompany: YesNo,
});
export type EligibiliteDraft = z.infer<typeof EligibiliteDraft>;

export const SocieteDraft = z.object({
  name: Short,
  /** « Je confirme utiliser … malgré les homonymes potentiels » — cleared by the API when the name changes. */
  nameConfirmed: z.boolean().optional(),
  hasTradeName: YesNo,
  tradeName: z.string().max(200).optional(),
  acronym: Short,
  durationYears: z.number().int().nullable().optional(),
  closingDay: z.number().int().nullable().optional(),
  closingMonth: z.number().int().min(1).max(12).nullable().optional(),
  firstClosingDate: DateOrEmpty,
  capitalCents: Cents.nullable().optional(),
  /** SASU: shares of 1 € (computed); EURL: number of « parts sociales », chosen. */
  sharesCount: z.number().int().nullable().optional(),
  paidInCents: Cents.nullable().optional(),
});
export type SocieteDraft = z.infer<typeof SocieteDraft>;

export const PersonnesDraft = z.object({
  associate: PersonDraft.optional(),
  /** SASU: « Êtes-vous le président ? » · EURL: « Êtes-vous le gérant ? » */
  isLeader: YesNo,
  leader: PersonDraft.optional(),
  /** SASU: directeur général · EURL: co-gérant. */
  hasExtraLeader: YesNo,
  extraLeader: PersonDraft.optional(),
});
export type PersonnesDraft = z.infer<typeof PersonnesDraft>;

export const FiscalDraft = z.object({
  incomeTax: z.enum(["IS", "IR"]).nullable().optional(),
  vatFranchise: YesNo,
});
export type FiscalDraft = z.infer<typeof FiscalDraft>;

export const EmployeeCounts = z.object({
  employees: z.number().int().min(0).max(9999).optional(),
  apprentices: z.number().int().min(0).max(9999).optional(),
  vrp: z.number().int().min(0).max(9999).optional(),
  seasonal: z.number().int().min(0).max(9999).optional(),
  sailors: z.number().int().min(0).max(9999).optional(),
});

/** Seat of a company (step 5) or address of an auto-entreprise (AE step 5). */
export const SiegeDraft = z.object({
  atHome: YesNo,
  /** Mandatory acknowledgement of the legal publicity of a home address. */
  legalNoticeAck: z.boolean().optional(),
  proType: z.enum(["domiciliation", "lease", "third_party"]).nullable().optional(),
  domiciliation: z
    .object({ siren: Short, name: Short, postcode: Short, city: Short, struckOff: z.boolean().optional() })
    .optional(),
  thirdParty: z
    .object({
      kind: z.enum(["person", "company"]).nullable().optional(),
      lastName: Short,
      firstName: Short,
      birthDate: DateOrEmpty,
      companyName: Short,
      siren: Short,
      address: AddressDraft.optional(),
      email: Short,
    })
    .optional(),
  address: AddressDraft.optional(),
  employeesInEstablishment: YesNo,
  hasEmployees: YesNo,
  employeeCounts: EmployeeCounts.optional(),
});
export type SiegeDraft = z.infer<typeof SiegeDraft>;

export const QualificationDraft = z.object({
  holder: z.enum(["self", "other", "recruit"]).nullable().optional(),
  person: z
    .object({
      firstNames: Short,
      lastName: Short,
      gender: z.enum(["M", "F", "X"]).nullable().optional(),
      birthDate: DateOrEmpty,
      birthCountry: Short,
      email: Short,
      phone: Short,
    })
    .optional(),
  recruitCommitment: z.boolean().optional(),
});
export type QualificationDraft = z.infer<typeof QualificationDraft>;

export const ActivityDraft = z.object({
  id: z.string().max(40),
  description: Text,
  /** Text the client had typed before « Utiliser cette clause » (undo). */
  previousDescription: Text,
  qualification: QualificationDraft.optional(),
});
export type ActivityDraft = z.infer<typeof ActivityDraft>;

export const ActivitesDraft = z.object({
  /** AE only: between 30 days ago and today. */
  startDate: DateOrEmpty,
  exercise: z.enum(["permanent", "seasonal"]).nullable().optional(),
  seasonStart: DateOrEmpty,
  seasonEnd: DateOrEmpty,
  /** Companies only (AE answers it in « cotisations »). */
  ambulant: YesNo,
  primary: ActivityDraft.optional(),
  hasSecondary: YesNo,
  secondaries: z.array(ActivityDraft).max(30).optional(),
});
export type ActivitesDraft = z.infer<typeof ActivitesDraft>;

export const SirenCheckStatus = z.enum(["ceased", "ceased_recently", "active", "not_found", "failed", "unavailable"]);
export const SirenCheck = z.object({
  status: SirenCheckStatus,
  name: z.string().nullable(),
  address: z.string().nullable(),
  checkedAt: IsoDateTime,
});
export type SirenCheck = z.infer<typeof SirenCheck>;

export const AnterioriteDraft = z.object({
  wasAE: YesNo,
  oldSiren: Short,
  sirenCheck: SirenCheck.nullable().optional(),
  cape: YesNo,
  /** Renunciation of the default unseizability of the main residence (stop). */
  renonciation: YesNo,
});
export type AnterioriteDraft = z.infer<typeof AnterioriteDraft>;

export const IdentiteDraft = z.object({
  declarant: PersonDraft.optional(),
  /** Write-only: the API never sends the NIR back, only `nirLast4` on the form. */
  nir: Short,
  insurer: z.enum(["cpam", "msa", "other"]).nullable().optional(),
  specialInsurer: Short,
  insurerOther: Short,
  hasTradeName: YesNo,
  tradeName: z.string().max(200).optional(),
});
export type IdentiteDraft = z.infer<typeof IdentiteDraft>;

export const SituationDraft = z.object({
  previousActivity: YesNo,
  previousDescription: Text,
  previousEndDate: DateOrEmpty,
  previousCountry: Short,
  currentActivity: YesNo,
  currentStatus: z.enum(["employee", "farm_employee", "retired", "self_employed", "disability", "other"]).nullable().optional(),
  currentStatusOther: Short,
});
export type SituationDraft = z.infer<typeof SituationDraft>;

export const CotisationsDraft = z.object({
  frequency: z.enum(["monthly", "quarterly"]).nullable().optional(),
  liberatoire: YesNo,
  acre: YesNo,
  ambulant: YesNo,
  nonSedentary: z.enum(["eu_non_resident", "itinerant"]).nullable().optional(),
});
export type CotisationsDraft = z.infer<typeof CotisationsDraft>;

/** Every step's draft. A SASU/EURL form uses the six company keys, an AE form the six AE keys. */
export const FormAnswers = z.object({
  eligibilite: EligibiliteDraft,
  societe: SocieteDraft,
  personnes: PersonnesDraft,
  fiscal: FiscalDraft,
  siege: SiegeDraft,
  activites: ActivitesDraft,
  anteriorite: AnterioriteDraft,
  identite: IdentiteDraft,
  situation: SituationDraft,
  cotisations: CotisationsDraft,
  adresse: SiegeDraft,
  activite: ActivitesDraft,
}).partial();
export type FormAnswers = z.infer<typeof FormAnswers>;
export type StepDraft<K extends StepKey> = NonNullable<FormAnswers[K]>;

/** Body of the autosave PATCH: the whole draft of one step. */
export const SaveStepBody = z.discriminatedUnion("step", [
  z.object({ step: z.literal("eligibilite"), values: EligibiliteDraft }),
  z.object({ step: z.literal("societe"), values: SocieteDraft }),
  z.object({ step: z.literal("personnes"), values: PersonnesDraft }),
  z.object({ step: z.literal("fiscal"), values: FiscalDraft }),
  z.object({ step: z.literal("siege"), values: SiegeDraft }),
  z.object({ step: z.literal("activites"), values: ActivitesDraft }),
  z.object({ step: z.literal("anteriorite"), values: AnterioriteDraft }),
  z.object({ step: z.literal("identite"), values: IdentiteDraft }),
  z.object({ step: z.literal("situation"), values: SituationDraft }),
  z.object({ step: z.literal("cotisations"), values: CotisationsDraft }),
  z.object({ step: z.literal("adresse"), values: SiegeDraft }),
  z.object({ step: z.literal("activite"), values: ActivitesDraft }),
]);
export type SaveStepBody = z.infer<typeof SaveStepBody>;

/** A « Oui » that stops the online journey (boards 45, 58): recorded on the form; the callback opens on « Parler à un conseiller » (A4). */
export const HardStop = z.object({
  step: StepKey,
  field: z.string(),
  /** The question as shown: « Activité agricole ? ». */
  question: z.string(),
  /** Short reason for the callback card: « activité agricole ». */
  reason: z.string(),
  at: IsoDateTime,
});
export type HardStop = z.infer<typeof HardStop>;

export const CreationForm = z.object({
  dossierId: Id,
  reference: z.string(),
  legalForm: CreationLegalForm,
  status: DossierStatus,
  answers: FormAnswers,
  /** Steps whose answers pass validation (overview + step strip). */
  completedSteps: z.array(StepKey),
  /** Last step the client saved, to resume there. */
  lastStep: StepKey.nullable(),
  hardStop: HardStop.nullable(),
  /** AE: last 4 digits of the saved NIR, never the full number. */
  nirLast4: z.string().nullable(),
  /** Creation bundled with Melrys accounting: the « contrat d'appui » question is not asked (#30). */
  withAccounting: z.boolean().optional(),
  updatedAt: IsoDateTime,
});
export type CreationForm = z.infer<typeof CreationForm>;

// ———————————————————————————————————————————————————————————————
// Shared business rules (client + API)
// ———————————————————————————————————————————————————————————————

/** EU + EEA + Switzerland: other nationalities trigger the conditional-documents review. */
export const EU_EEA_COUNTRIES = [
  "France", "Allemagne", "Autriche", "Belgique", "Bulgarie", "Chypre", "Croatie", "Danemark", "Espagne", "Estonie", "Finlande", "Grèce",
  "Hongrie", "Irlande", "Italie", "Lettonie", "Lituanie", "Luxembourg", "Malte", "Pays-Bas", "Pologne", "Portugal", "Roumanie",
  "Slovaquie", "Slovénie", "Suède", "Tchéquie", "Islande", "Liechtenstein", "Norvège", "Suisse",
];

const ARTISANAL_KEYWORDS = [
  "plomberie", "plombier", "chauffage", "chaudière", "électricité", "électricien", "electricien", "maçonnerie", "maçon", "coiffure", "coiffeur",
  "esthétique", "esthéticien", "boulangerie", "pâtisserie", "boucherie", "charcuterie", "ramonage", "réparation automobile", "carrosserie",
  "toiture", "couvreur", "climatisation", "pompe à chaleur", "pompes à chaleur",
  // Building trades, vehicles, and the other activities of art. 16 of loi n° 96-603 (qualification required).
  "menuiserie", "menuisier", "charpente", "charpentier", "peinture en bâtiment", "plâtrerie", "plâtrier", "plaquiste", "carrelage", "carreleur",
  "serrurerie", "serrurier", "vitrerie", "vitrier", "agencement", "isolation", "terrassement", "étanchéité", "zinguerie", "parquet",
  "mécanique automobile", "garagiste", "prothèse dentaire", "prothésiste dentaire", "maréchal-ferrant", "poissonnerie", "glacier",
  // Trade names typed as free text, nail care (soins esthétiques), motorcycles (véhicules).
  "boulanger", "pâtissier", "boucher", "charcutier", "poissonnier", "chauffagiste", "frigoriste", "peintre en bâtiment", "installation électrique",
  "installations électriques", "onglerie", "prothésie ongulaire", "prothésiste ongulaire", "réparation de motocycles",
];
/** Whole words: « médecine douce » is not « médecin », « avocate » is « avocat ». */
const DEDICATED_FORM_PATTERNS = [
  /(^|[^\p{L}])avocat(e|s|es)?(?![\p{L}])/u,
  /(^|[^\p{L}])notaires?(?![\p{L}])/u,
  /(^|[^\p{L}])huissiers?(?![\p{L}])/u,
  /commissaires? de justice/u,
  /(^|[^\p{L}])médecins?(?![\p{L}])/u,
  /(^|[^\p{L}])pharmacien(ne)?s?(?![\p{L}])/u,
  /experts?-comptables?|expertise comptable/u,
  /(^|[^\p{L}])vétérinaires?(?![\p{L}])/u,
];

export type Regulation = "artisanal" | "dedicated_form" | null;

/** Regulated-activity detection from the description (board 54): no question asked to the client. */
export function detectRegulation(description: string | undefined | null): Regulation {
  const d = (description ?? "").toLowerCase();
  if (d.trim().length < 3) return null;
  if (DEDICATED_FORM_PATTERNS.some((k) => k.test(d))) return "dedicated_form";
  if (ARTISANAL_KEYWORDS.some((k) => d.includes(k))) return "artisanal";
  return null;
}

/** Legal-form words ignored when comparing business names (« Atelier Nord SAS » = « atelier nord »). */
const LEGAL_FORM_WORDS = new Set(["sas", "sasu", "sarl", "eurl", "sa", "snc", "sci", "scop", "scea", "sel", "selarl", "selas", "selafa", "eirl", "ei", "ae", "scm", "scp", "gie", "sca", "scs", "earl", "gaec"]);
const LEGAL_FORM_PHRASES = [
  "societe par actions simplifiee unipersonnelle", "societe par actions simplifiee", "societe a responsabilite limitee", "entreprise unipersonnelle a responsabilite limitee",
  "entreprise individuelle a responsabilite limitee", "entreprise individuelle", "societe anonyme", "auto entrepreneur", "micro entrepreneur", "micro entreprise",
];

/**
 * Comparison key of a business name (dénomination, nom commercial) for the « already used on Melrys » rule: case,
 * accents, punctuation, spacing and legal-form words ignored; « & » reads « et ». Empty when nothing distinctive is left.
 */
export function businessNameKey(name: string | null | undefined): string {
  let s = (name ?? "")
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/&/g, " et ")
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  for (const p of LEGAL_FORM_PHRASES) s = ` ${s} `.replace(` ${p} `, " ").trim();
  return s
    .split(" ")
    .filter((w) => w && !LEGAL_FORM_WORDS.has(w))
    .join("");
}

export const melrysNameTakenMessage = (name: string) =>
  `« ${name.trim()} » est déjà utilisé par une autre entreprise accompagnée par Melrys. Choisissez un autre nom.`;

const MONTH_NAMES = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MAX_DAYS = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** « Cette date n'existe pas : avril compte au maximum 30 jours. » — null when the day/month pair exists. */
export function closingDayError(day: number | null | undefined, month: number | null | undefined): string | null {
  if (!day || !month) return null;
  if (day < 1) return "Ce jour n'existe pas.";
  const max = MAX_DAYS[month - 1]!;
  return day > max ? `Cette date n'existe pas : ${MONTH_NAMES[month - 1]} compte au maximum ${max} jours.` : null;
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseIso = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000);

/** First closing: the first day/month occurrence that leaves a first financial year of more than 6 months. */
export function computeFirstClosing(day: number, month: number, today: Date): string {
  for (let year = today.getFullYear(); ; year++) {
    const last = new Date(year, month, 0).getDate();
    const candidate = new Date(year, month - 1, Math.min(day, last));
    if (daysBetween(today, candidate) > 183) return iso(candidate);
  }
}

export function isMinor(p: PersonDraft | undefined, today: Date): boolean {
  if (!p?.birthYear) return false;
  const birth = new Date(p.birthYear, (p.birthMonth ?? 1) - 1, p.birthDay ?? 1);
  const eighteen = new Date(birth.getFullYear() + 18, birth.getMonth(), birth.getDate());
  return eighteen > today;
}

/** Non-EU nationality, non-French tax residence or minor → manual review with extra documents. */
export function needsConditionalDocuments(p: PersonDraft | undefined, today: Date): boolean {
  if (!p) return false;
  if (p.nationality && !EU_EEA_COUNTRIES.includes(p.nationality)) return true;
  if (p.taxResidence && p.taxResidence !== "France") return true;
  return isMinor(p, today);
}

/**
 * Questions whose « Oui » stops the online journey, in the order they are asked. `when`: another stopping answer than
 * `true` (the health insurer « Autre / je ne sais pas », #35); `reset`: the value « Annuler » puts back (default `false`).
 */
export const HARD_STOP_QUESTIONS: { step: StepKey; field: string; question: string; reason: string; forms: CreationLegalForm[]; when?: (v: unknown) => boolean; reset?: unknown }[] = [
  { step: "eligibilite", field: "agricultural", question: "Activité agricole ?", reason: "activité agricole", forms: ["SASU", "EURL"] },
  { step: "eligibilite", field: "origin", question: "Votre entreprise résulte-t-elle d'une de ces situations ?", reason: "fusion, scission ou reprise", forms: ["SASU", "EURL"] },
  { step: "eligibilite", field: "cape", question: "Bénéficiez-vous d'un contrat d'appui ?", reason: "contrat d'appui (Cape)", forms: ["SASU", "EURL"] },
  { step: "eligibilite", field: "mission", question: "Société à mission ?", reason: "société à mission", forms: ["SASU", "EURL"] },
  { step: "eligibilite", field: "ess", question: "Démarche d'économie sociale et solidaire ?", reason: "économie sociale et solidaire", forms: ["SASU", "EURL"] },
  { step: "eligibilite", field: "presidentIsCompany", question: "Le président sera-t-il une société ?", reason: "président personne morale", forms: ["SASU"] },
  { step: "anteriorite", field: "cape", question: "Bénéficiez-vous d'un contrat d'appui ?", reason: "contrat d'appui (Cape)", forms: ["AE"] },
  {
    step: "anteriorite",
    field: "renonciation",
    question: "Avez-vous fait une déclaration de renoncement de l'insaisissabilité de droit ?",
    reason: "renoncement à l'insaisissabilité",
    forms: ["AE"],
  },
  {
    step: "identite",
    field: "insurer",
    question: "Organisme d'assurance maladie : « Autre / je ne sais pas »",
    reason: "régime d'assurance maladie à vérifier",
    forms: ["AE"],
    when: (v) => v === "other",
    reset: null,
  },
];

export function findHardStop(form: CreationLegalForm, a: FormAnswers, opts: { withAccounting?: boolean } = {}) {
  return (
    HARD_STOP_QUESTIONS.find((q) => {
      if (!q.forms.includes(form)) return false;
      if (q.field === "cape" && opts.withAccounting) return false; // not asked when accounting is bundled
      const step = a[q.step] as Record<string, unknown> | undefined;
      return q.when ? q.when(step?.[q.field]) : step?.[q.field] === true;
    }) ?? null
  );
}

// ——— Validation ———

export interface StepIssue {
  /** Dotted path inside the step draft, e.g. "associate.lastName". */
  path: string;
  /** Message shown under the field. */
  message: string;
  /** Readable field name for the « Envoi impossible » window: « Associé unique — Situation matrimoniale ». */
  label: string;
  /** Section named in the « Il manque : … » toast: « Identité de l'associé unique ». */
  group: string;
}

export interface ValidationCtx {
  form: CreationLegalForm;
  today: Date;
  /** AE: a NIR is already stored server-side. */
  nirSaved?: boolean;
  /** Creation bundled with accounting: no « contrat d'appui » question (creation-only plans only). */
  withAccounting?: boolean;
}

const REQUIRED = "Ce champ est requis.";
const blank = (s: string | null | undefined) => !s || !s.trim();
const digits = (s: string | null | undefined) => (s ?? "").replace(/\D/g, "");
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const euros = (cents: number) => new Intl.NumberFormat("fr-FR").format(Math.round(cents / 100)).replace(/ /g, " ");

class Issues {
  list: StepIssue[] = [];
  add(path: string, message: string, label: string, group: string) {
    this.list.push({ path, message, label, group });
  }
}

function validateAddress(out: Issues, a: AddressDraft | undefined, prefix: string, who: string, group: string, withCountry = false) {
  if (blank(a?.street)) out.add(`${prefix}.street`, REQUIRED, `${who} — Adresse`, group);
  const cp = a?.postcode ?? "";
  if (blank(cp)) out.add(`${prefix}.postcode`, REQUIRED, `${who} — Code postal`, group);
  else if ((!a?.country || a.country === "France") && !/^\d{5}$/.test(cp.trim())) out.add(`${prefix}.postcode`, "Le code postal doit comporter 5 chiffres.", `${who} — Code postal`, group);
  if (blank(a?.city)) out.add(`${prefix}.city`, REQUIRED, `${who} — Ville`, group);
  if (withCountry && blank(a?.country)) out.add(`${prefix}.country`, REQUIRED, `${who} — Pays`, group);
}

function validatePerson(out: Issues, p: PersonDraft | undefined, prefix: string, who: string, group: string, opts: { marital: boolean; today: Date }) {
  const at = (f: string) => (prefix ? `${prefix}.${f}` : f);
  if (blank(p?.firstNames?.[0])) out.add(at("firstNames.0"), REQUIRED, `${who} — Prénom`, group);
  if (blank(p?.lastName)) out.add(at("lastName"), REQUIRED, `${who} — Nom de naissance`, group);
  // G1/C04 (retour 3): « Autre » is no longer offered: a new "X" is asked again; an old one with its INPI civilité still passes.
  if (!p?.gender || (p.gender === "X" && !p.inpiGender)) out.add(at("gender"), REQUIRED, `${who} — Genre`, group);
  if (!p?.birthYear) out.add(at("birthYear"), REQUIRED, `${who} — Date de naissance`, group);
  else if (!p.noBirthDayMonth) {
    if (!p.birthDay || !p.birthMonth) out.add(at("birthDay"), "Indiquez le jour et le mois de naissance.", `${who} — Date de naissance`, group);
    else {
      const d = new Date(p.birthYear, p.birthMonth - 1, p.birthDay);
      if (d.getMonth() !== p.birthMonth - 1) out.add(at("birthDay"), "Cette date n'existe pas.", `${who} — Date de naissance`, group);
      else if (d > opts.today) out.add(at("birthYear"), "La date de naissance ne peut pas être dans le futur.", `${who} — Date de naissance`, group);
    }
  }
  if (isMinor(p, opts.today) && !p?.emancipated)
    out.add(at("emancipated"), "Un mineur doit être émancipé pour créer cette entreprise : cochez la case si c'est votre cas.", `${who} — Émancipation`, group);
  if (blank(p?.birthCity)) out.add(at("birthCity"), REQUIRED, `${who} — Commune de naissance`, group);
  if (blank(p?.birthCountry)) out.add(at("birthCountry"), REQUIRED, `${who} — Pays de naissance`, group);
  if (blank(p?.nationality)) out.add(at("nationality"), REQUIRED, `${who} — Nationalité`, group);
  if (opts.marital && !p?.maritalStatus) out.add(at("maritalStatus"), REQUIRED, `${who} — Situation matrimoniale`, group);
  validateAddress(out, p?.address, at("address"), who, group);
  const phone = digits(p?.phone);
  if (!phone) out.add(at("phone"), "Votre numéro de téléphone est requis.", `${who} — Téléphone`, group);
  else if (phone.replace(/^0/, "").length !== 9) out.add(at("phone"), "Numéro de téléphone invalide.", `${who} — Téléphone`, group);
  if (blank(p?.email)) out.add(at("email"), REQUIRED, `${who} — Adresse email`, group);
  else if (!EMAIL_RE.test(p!.email!.trim())) out.add(at("email"), "Adresse email invalide.", `${who} — Adresse email`, group);
}

function validateYes(out: Issues, v: boolean | null | undefined, path: string, label: string, group = label) {
  if (v !== true && v !== false) out.add(path, "Choisissez une réponse.", label, group);
}

function validateQualification(out: Issues, q: QualificationDraft | undefined, prefix: string, who: string) {
  const group = `${who} — qualification professionnelle`;
  if (!q?.holder) return out.add(`${prefix}.holder`, "Précisez qui détient la qualification professionnelle.", group, group);
  if (q.holder === "other") {
    const p = q.person;
    if (blank(p?.firstNames) || blank(p?.lastName) || (blank(p?.email) && blank(p?.phone)))
      out.add(`${prefix}.person`, "Renseignez la personne qualifiée (prénom, nom, contact).", `${who} — personne qualifiée`, group);
    else if (!blank(p?.email) && !EMAIL_RE.test(p!.email!.trim())) out.add(`${prefix}.person.email`, "Adresse email invalide.", `${who} — personne qualifiée`, group);
  }
  if (q.holder === "recruit" && !q.recruitCommitment) out.add(`${prefix}.recruitCommitment`, "Cochez votre engagement pour envoyer votre dossier.", `${who} — engagement de recrutement`, group);
}

function validateActivity(out: Issues, a: ActivityDraft | undefined, prefix: string, who: string) {
  const d = a?.description?.trim() ?? "";
  if (d.length < 10) return out.add(`${prefix}.description`, "Décrivez l'activité (10 caractères minimum).", who, who);
  const reg = detectRegulation(d);
  if (reg === "dedicated_form")
    out.add(`${prefix}.description`, "Cette activité relève d'une profession réglementée qui nécessite une structure dédiée : contactez Melrys avant de poursuivre.", `${who} — forme juridique dédiée requise`, who);
  if (reg === "artisanal") validateQualification(out, a?.qualification, `${prefix}.qualification`, who);
}

function validateSiege(out: Issues, s: SiegeDraft | undefined, ae: boolean) {
  const addrGroup = ae ? "Adresse de l'entreprise" : "Adresse du siège social";
  validateYes(out, s?.atHome, "atHome", "Domicile personnel", addrGroup);
  if (s?.atHome === true && !s.legalNoticeAck)
    out.add("legalNoticeAck", "Cochez la case pour continuer. Votre adresse personnelle apparaîtra dans les registres publics.", "Prise de connaissance de la publicité de l'adresse", "Prise de connaissance de la publicité de l'adresse");
  if (s?.atHome === false) {
    const g = "Adresse professionnelle";
    if (!s.proType) out.add("proType", "Choisissez une réponse.", g, g);
    if (s.proType === "domiciliation") {
      const dom = s.domiciliation;
      if (!/^\d{9}$/.test(digits(dom?.siren))) out.add("domiciliation.siren", "Le SIREN doit comporter exactement 9 chiffres.", "SIREN de l'entreprise domiciliataire", g);
      if (blank(dom?.name)) out.add("domiciliation.name", REQUIRED, "Nom de l'entreprise domiciliataire", g);
      if (dom?.struckOff) out.add("domiciliation.siren", "Cette société de domiciliation est radiée — cet établissement ne peut plus héberger un siège social.", "Société de domiciliation radiée", g);
    }
    if (s.proType === "third_party") {
      const t = s.thirdParty;
      if (!t?.kind) out.add("thirdParty.kind", "Choisissez une réponse.", "Type de tiers", g);
      if (t?.kind === "person") {
        if (blank(t.lastName)) out.add("thirdParty.lastName", REQUIRED, "Tiers — Nom", g);
        if (blank(t.firstName)) out.add("thirdParty.firstName", REQUIRED, "Tiers — Prénom", g);
      }
      if (t?.kind === "company") {
        if (blank(t.companyName)) out.add("thirdParty.companyName", REQUIRED, "Tiers — Raison sociale", g);
        if (!/^\d{9}$/.test(digits(t.siren))) out.add("thirdParty.siren", "Le SIREN doit comporter exactement 9 chiffres.", "Tiers — Numéro SIREN", g);
      }
      if (t?.kind) {
        validateAddress(out, t.address, "thirdParty.address", "Tiers", g);
        if (blank(t.email)) out.add("thirdParty.email", REQUIRED, "Adresse e-mail du tiers", g);
        else if (!EMAIL_RE.test(t.email!.trim())) out.add("thirdParty.email", "Adresse email invalide.", "Adresse e-mail du tiers", g);
      }
    }
  }
  validateAddress(out, s?.address, "address", ae ? "Adresse de l'entreprise" : "Siège social", addrGroup, true);
  validateYes(out, s?.employeesInEstablishment, "employeesInEstablishment", "Présence de salariés dans l'établissement", "Salariés");
  validateYes(out, s?.hasEmployees, "hasEmployees", "Salariés", "Salariés");
  if (s?.hasEmployees === true) {
    const total = Object.values(s.employeeCounts ?? {}).reduce<number>((n, v) => n + (v ?? 0), 0);
    if (total < 1) out.add("employeeCounts", "Indiquez au moins un salarié.", "Nombre de salariés", "Salariés");
  }
}

/**
 * Key of an activity description for the « same activity twice » rule: case, accents, punctuation and spacing
 * ignored, so a clause used twice (same wording) or the same free text collide.
 */
export function activityKey(description: string | null | undefined): string {
  return (description ?? "")
    .toLowerCase()
    .replace(/œ/g, "oe")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** « Activité principale » / « Activité secondaire 2 »: who already holds this description (null when unique). */
export function duplicateActivityOf(a: ActivitesDraft | undefined, index: number | "primary"): string | null {
  const list = a?.secondaries ?? [];
  const self = index === "primary" ? a?.primary : list[index];
  const key = activityKey(self?.description);
  if (key.length < 3 || index === "primary") return null;
  if (activityKey(a?.primary?.description) === key) return "votre activité principale";
  const j = list.findIndex((x, i) => i < index && activityKey(x.description) === key);
  return j >= 0 ? `votre activité secondaire ${j + 1}` : null;
}

export const duplicateActivityMessage = (holder: string) => `Cette activité est déjà déclarée comme ${holder} : une même activité ne peut être choisie qu'une fois.`;

function validateActivities(out: Issues, a: ActivitesDraft | undefined, ctx: ValidationCtx) {
  const ae = ctx.form === "AE";
  if (ae) {
    if (!a?.startDate) out.add("startDate", REQUIRED, "Date de début d'activité", "Date de début d'activité");
    else {
      const days = daysBetween(parseIso(a.startDate), new Date(ctx.today.getFullYear(), ctx.today.getMonth(), ctx.today.getDate()));
      if (days > 30) out.add("startDate", "La date de début d'activité ne peut pas être antérieure à 30 jours.", "Date de début d'activité", "Date de début d'activité");
      if (days < 0) out.add("startDate", "La date de début d'activité ne peut pas être dans le futur.", "Date de début d'activité", "Date de début d'activité");
    }
  }
  if (!a?.exercise) out.add("exercise", "Choisissez une réponse.", "Exercice de l'activité", "Exercice de l'activité");
  if (a?.exercise === "seasonal") {
    if (!a.seasonStart) out.add("seasonStart", REQUIRED, "Date de début de période", "Exercice de l'activité");
    if (!a.seasonEnd) out.add("seasonEnd", REQUIRED, "Date de fin de période", "Exercice de l'activité");
    if (a.seasonStart && a.seasonEnd && a.seasonEnd <= a.seasonStart)
      out.add("seasonEnd", "La date de fin doit être postérieure à la date de début.", "Date de fin de période", "Exercice de l'activité");
  }
  if (!ae) validateYes(out, a?.ambulant, "ambulant", "Activité ambulante");
  validateActivity(out, a?.primary, "primary", "Activité principale");
  validateYes(out, a?.hasSecondary, "hasSecondary", "Activité secondaire");
  if (a?.hasSecondary === true) {
    const list = a.secondaries ?? [];
    if (list.length === 0) out.add("secondaries", "Ajoutez une activité secondaire, ou répondez « Non ».", "Activités secondaires", "Activités secondaires");
    list.forEach((s, i) => {
      validateActivity(out, s, `secondaries.${i}`, `Activité secondaire ${i + 1}`);
      const holder = duplicateActivityOf(a, i);
      if (holder) out.add(`secondaries.${i}.description`, duplicateActivityMessage(holder), `Activité secondaire ${i + 1} — activité en double`, "Activités secondaires");
    });
  }
}

/** Validates one step. Same rules on the client (field errors, « Il manque ») and in the API (submission). */
export function validateStep(step: StepKey, answers: FormAnswers, ctx: ValidationCtx): StepIssue[] {
  const out = new Issues();
  const sasu = ctx.form === "SASU";
  switch (step) {
    case "eligibilite": {
      const e = answers.eligibilite;
      validateYes(out, e?.agricultural, "agricultural", "Réponse activité agricole");
      validateYes(out, e?.origin, "origin", "Origine");
      if (!ctx.withAccounting) validateYes(out, e?.cape, "cape", "Contrat d'appui");
      validateYes(out, e?.mission, "mission", "Société à mission");
      validateYes(out, e?.ess, "ess", "Économie sociale et solidaire");
      if (sasu) validateYes(out, e?.presidentIsCompany, "presidentIsCompany", "Président personne morale");
      break;
    }
    case "societe": {
      const s = answers.societe;
      const g = "Dénomination sociale";
      if (blank(s?.name)) out.add("name", REQUIRED, "Nom de l'entreprise", g);
      else if (!s?.nameConfirmed) out.add("nameConfirmed", "Confirmez la dénomination pour continuer.", "Confirmation de la dénomination", g);
      validateYes(out, s?.hasTradeName, "hasTradeName", "Nom commercial");
      if (s?.hasTradeName && blank(s.tradeName)) out.add("tradeName", REQUIRED, "Nom commercial", "Nom commercial");
      const dur = s?.durationYears;
      if (dur == null) out.add("durationYears", REQUIRED, "Durée", "Durée");
      else if (dur < 1 || dur > 99) out.add("durationYears", "La durée doit être comprise entre 1 et 99 ans.", "Durée", "Durée");
      if (!s?.closingDay) out.add("closingDay", REQUIRED, "Jour de clôture annuelle", "Jour de clôture annuelle");
      else {
        const err = closingDayError(s.closingDay, s.closingMonth);
        if (err) out.add("closingDay", err, "Jour de clôture annuelle", "Jour de clôture annuelle");
      }
      if (!s?.closingMonth) out.add("closingMonth", REQUIRED, "Mois de clôture annuelle", "Mois de clôture annuelle");
      if (!s?.firstClosingDate) out.add("firstClosingDate", REQUIRED, "Date de première clôture", "Date de première clôture");
      else {
        const days = daysBetween(ctx.today, parseIso(s.firstClosingDate));
        if (days <= 183 || days > 731)
          out.add("firstClosingDate", "La première clôture doit intervenir entre 6 et 24 mois après la création.", "Date de première clôture", "Date de première clôture");
      }
      const cg = "Structure du capital";
      const cap = s?.capitalCents;
      if (!cap) out.add("capitalCents", cap === 0 ? "Le capital social doit être d'au moins 1 €." : REQUIRED, "Montant du capital social", cg);
      if (!sasu) {
        const n = s?.sharesCount;
        if (!n) out.add("sharesCount", REQUIRED, "Nombre de parts sociales", cg);
        else if (cap && cap % n !== 0)
          out.add(
            "sharesCount",
            `La valeur nominale doit tomber juste : le capital (${euros(cap)} €) n'est pas divisible sans reste par ${n} parts sociales. Ajustez le nombre.`,
            "Nombre de parts sociales",
            cg,
          );
      }
      const paid = s?.paidInCents;
      const pct = MIN_PAID_IN_PCT[sasu ? "SASU" : "EURL"];
      if (paid == null) out.add("paidInCents", REQUIRED, "Montant libéré à la constitution", cg);
      else if (cap && paid * 100 < cap * pct) out.add("paidInCents", `Le montant libéré doit être d'au moins ${pct} % du capital social.`, "Montant libéré à la constitution", cg);
      else if (cap && paid > cap) out.add("paidInCents", "Le montant libéré ne peut pas dépasser le capital social.", "Montant libéré à la constitution", cg);
      break;
    }
    case "personnes": {
      const p = answers.personnes;
      const lead = sasu ? "président" : "gérant";
      validatePerson(out, p?.associate, "associate", "Associé unique", "Identité de l'associé unique", { marital: true, today: ctx.today });
      validateYes(out, p?.isLeader, "isLeader", `Êtes-vous le ${lead} ?`);
      // C08: the president / gérant is a natural person (a company president stops the journey at step 1): no re-ask here.
      if (p?.isLeader === false) {
        validatePerson(out, p.leader, "leader", sasu ? "Président" : "Gérant", sasu ? "Identité du président" : "Identité du gérant", { marital: false, today: ctx.today });
      }
      validateYes(out, p?.hasExtraLeader, "hasExtraLeader", sasu ? "Directeur général" : "Co-gérant");
      if (p?.hasExtraLeader)
        validatePerson(out, p.extraLeader, "extraLeader", sasu ? "Directeur général" : "Co-gérant", sasu ? "Identité du directeur général" : "Identité du co-gérant", {
          marital: false,
          today: ctx.today,
        });
      break;
    }
    case "fiscal": {
      const f = answers.fiscal;
      if (!f?.incomeTax) out.add("incomeTax", "Choisissez une réponse.", "Impôt sur les bénéfices", "Impôt sur les bénéfices");
      validateYes(out, f?.vatFranchise, "vatFranchise", "Franchise en base de TVA");
      break;
    }
    case "siege": {
      validateSiege(out, answers.siege, false);
      // Company seat « au domicile » (art. L.123-11-1 C. com.) = the legal representative's home: with a third-party
      // président / gérant, the associate's home is not allowed (the attestation is signed by the representative).
      const st = answers.siege;
      const per = answers.personnes;
      const home = per?.isLeader === false ? per.leader?.address : undefined;
      const norm = (x?: string) => (x ?? "").trim().toLowerCase();
      if (st?.atHome && home?.street && st.address?.street && (norm(st.address.street) !== norm(home.street) || norm(st.address.postcode) !== norm(home.postcode)))
        out.add("address.street", "Au domicile : le siège doit être fixé à l'adresse personnelle du représentant légal (président / gérant).", "Adresse du siège (domicile du représentant légal)", "Siège au domicile");
      break;
    }
    case "adresse":
      validateSiege(out, answers.adresse, true);
      break;
    case "activites":
      validateActivities(out, answers.activites, ctx);
      break;
    case "activite":
      validateActivities(out, answers.activite, ctx);
      break;
    case "anteriorite": {
      const a = answers.anteriorite;
      const g = "Réponse antériorité auto-entrepreneur";
      validateYes(out, a?.wasAE, "wasAE", g);
      if (a?.wasAE && !/^\d{9}$/.test(digits(a.oldSiren))) out.add("oldSiren", blank(a.oldSiren) ? REQUIRED : "Le SIREN doit comporter exactement 9 chiffres.", "Ancien SIREN", g);
      if (!ctx.withAccounting) validateYes(out, a?.cape, "cape", "Contrat d'appui");
      validateYes(out, a?.renonciation, "renonciation", "Renoncement à l'insaisissabilité");
      break;
    }
    case "identite": {
      const i = answers.identite;
      validatePerson(out, i?.declarant, "declarant", "Déclarant", "Votre identité de déclarant", { marital: true, today: ctx.today });
      const nir = digits(i?.nir);
      if (nir && nir.length !== 15) out.add("nir", "Le NIR doit comporter exactement 15 chiffres (espaces ignorés).", "NIR (15 chiffres)", "NIR (15 chiffres)");
      else if (!nir && !ctx.nirSaved) out.add("nir", REQUIRED, "NIR (15 chiffres)", "NIR (15 chiffres)");
      const g = "Organisme d’assurance maladie";
      if (!i?.insurer) out.add("insurer", REQUIRED, g, g);
      if (i?.insurer === "other") {
        if (blank(i.specialInsurer)) out.add("specialInsurer", REQUIRED, "Précision de l'organisme", g);
        else if (i.specialInsurer === "free" && blank(i.insurerOther)) out.add("insurerOther", REQUIRED, "Précision de l'organisme", g);
      }
      validateYes(out, i?.hasTradeName, "hasTradeName", "Nom commercial");
      if (i?.hasTradeName && blank(i.tradeName)) out.add("tradeName", REQUIRED, "Nom commercial", "Nom commercial");
      break;
    }
    case "situation": {
      const s = answers.situation;
      const g1 = "Activité antérieure non salariée";
      validateYes(out, s?.previousActivity, "previousActivity", g1);
      if (s?.previousActivity) {
        if (blank(s.previousDescription)) out.add("previousDescription", REQUIRED, "Activité antérieure — description", g1);
        if (s.previousEndDate && s.previousEndDate > iso(ctx.today)) out.add("previousEndDate", "La date de fin ne peut pas être dans le futur.", "Activité antérieure — date de fin", g1);
        if (blank(s.previousCountry)) out.add("previousCountry", REQUIRED, "Activité antérieure — pays", g1);
      }
      const g2 = "Activité actuelle";
      validateYes(out, s?.currentActivity, "currentActivity", g2);
      if (s?.currentActivity) {
        if (!s.currentStatus) out.add("currentStatus", REQUIRED, "Statut de l'activité actuelle", g2);
        if (s.currentStatus === "other" && blank(s.currentStatusOther)) out.add("currentStatusOther", REQUIRED, "Activité actuelle — description", g2);
      }
      break;
    }
    case "cotisations": {
      const c = answers.cotisations;
      if (!c?.frequency) out.add("frequency", "Choisissez une réponse.", "Rythme des cotisations", "Rythme des cotisations");
      validateYes(out, c?.liberatoire, "liberatoire", "Versement libératoire");
      validateYes(out, c?.acre, "acre", "ACRE");
      validateYes(out, c?.ambulant, "ambulant", "Statut ambulant");
      if (c?.ambulant && !c.nonSedentary) out.add("nonSedentary", "Choisissez une réponse.", "Qualité de non-sédentarité", "Qualité de non-sédentarité");
      break;
    }
  }
  return out.list;
}

/** Unique sections of a list of issues, for « Il manque : Dénomination sociale, Structure du capital ». */
export const missingGroups = (issues: StepIssue[]) => [...new Set(issues.map((i) => i.group))];

// ———————————————————————————————————————————————————————————————
// Advisor call, callback, lookups
// ———————————————————————————————————————————————————————————————

export const CallMode = z.enum(["visio", "phone"]);
export const AdvisorSlots = z.object({
  advisor: z.object({ name: z.string(), role: z.string() }),
  days: z.array(z.object({ date: IsoDate, slots: z.array(z.string().regex(/^\d{2}:\d{2}$/)) })),
});
export const Appointment = z.object({
  id: Id,
  start: IsoDateTime,
  end: IsoDateTime,
  mode: CallMode,
  phone: z.string().nullable(),
  meetUrl: z.string().nullable(),
});
export type Appointment = z.infer<typeof Appointment>;

export const Callback = z.object({
  id: Id,
  reason: z.string(),
  question: z.string(),
  phone: z.string(),
  status: z.enum(["open", "handled"]),
  createdAt: IsoDateTime,
});
export type Callback = z.infer<typeof Callback>;

export const NameSearchResult = z.object({
  /** Registered companies with a close name (indicative, never blocking). */
  homonyms: z.array(z.object({ name: z.string(), siren: z.string(), city: z.string() })),
  /** The name is already used by another Melrys dossier or company (normalised: case, accents, legal form): blocking. */
  takenByMelrys: z.boolean(),
});

export const AddressSuggestion = z.object({ label: z.string(), street: z.string(), postcode: z.string(), city: z.string() });
/** `postcode`: set when the search was a postal code (the commune or arrondissement it belongs to). */
export const CommuneSuggestion = z.object({ name: z.string(), dept: z.string(), deptName: z.string(), region: z.string(), postcode: z.string().optional() });
export const Domiciliataire = z.object({ name: z.string(), siren: z.string(), postcode: z.string(), city: z.string(), struckOff: z.boolean() });
export const ClauseSuggestion = z.object({
  /** Clause id (contracts/creation-activities.ts). */
  id: z.string(),
  title: z.string(),
  category: z.string(),
  clause: z.string(),
  /** NAF rév. 2 code proposed for this clause (the agent confirms it). */
  ape: z.string(),
  /** Regulated activity without the artisan qualification step: what the client must hold or declare. */
  notice: z.string().nullable(),
});
/** Melrys-only name check (debounced while the client types; no INSEE call). */
export const NameAvailability = z.object({
  /** Another Melrys dossier or company already uses this name (dénomination or nom commercial): blocking. */
  takenByMelrys: z.boolean(),
});

/** Error codes of the creation endpoints (ApiErrorBody.code). */
export const CreationErrorCode = z.enum([
  "dossier_exists", // « Vous avez déjà une demande de création en cours sur ce compte… »
  "company_exists", // « Une entreprise a déjà été créée avec ce compte… »
  "company_registered", // « Votre compte est déjà rattaché à une entreprise immatriculée… »
  "form_unavailable", // « Cette forme juridique arrive bientôt. »
  "service_locked", // « Ce service est déjà en cours sur votre compte : le choix est verrouillé. »
  "dossier_engaged", // « Ce dossier est déjà en cours de traitement… »
  "no_dossier",
  "form_locked", // the questionnaire was already sent
  "dossier_incomplete", // fields: { "<step>.<path>": "<label>" }
  "hard_stop",
  "dedicated_form_required",
  "name_taken", // fields: { "<step>.<path>": "<label>" } — dénomination / nom commercial used by another Melrys company
  "slot_taken", // « Ce créneau n'est plus disponible — choisissez-en un autre. »
  "address_service_down",
  "insee_unavailable",
]);

export const creationApi = {
  /** The account's dossier (one company per account), or null if none started. */
  current: endpoint({ id: "creation.current", method: "GET", path: "/creation/dossier", response: z.object({ dossier: Dossier.nullable() }) }),

  /** Board MX1-V1: records the service the client starts with. */
  chooseService: endpoint({
    id: "creation.chooseService",
    method: "POST",
    path: "/onboarding/service",
    body: z.object({ service: z.enum(["creation", "accounting"]) }),
    response: z.object({ services: z.array(z.enum(["creation", "accounting", "invoicing"])) }),
  }),

  /** Boards 39/40 « Démarrer le dossier »: creates the draft dossier and the company record. */
  start: endpoint({
    id: "creation.start",
    method: "POST",
    path: "/creation/dossier",
    body: z.object({ legalForm: LegalForm, withAccounting: z.boolean() }),
    response: z.object({ dossier: Dossier }),
  }),

  /** Board 35 « Supprimer et recommencer »: drops a draft (never an engaged dossier). */
  discardDraft: endpoint({ id: "creation.discardDraft", method: "DELETE", path: "/creation/dossier", response: Ok }),

  form: endpoint({ id: "creation.form", method: "GET", path: "/creation/dossier/form", response: z.object({ form: CreationForm }) }),

  /** Autosave of one step (debounced on the client). A stop answer is recorded (form.hardStop) but notifies no one (A4). */
  saveStep: endpoint({ id: "creation.saveStep", method: "PATCH", path: "/creation/dossier/form", body: SaveStepBody, response: z.object({ form: CreationForm }) }),

  /** Board 53 « Envoyer mon dossier pour vérification ». Next screen: creation-fee payment (tracking). */
  submit: endpoint({ id: "creation.submit", method: "POST", path: "/creation/dossier/submit", response: z.object({ dossier: Dossier }) }),

  nameSearch: endpoint({ id: "creation.nameSearch", method: "GET", path: "/creation/name-search", query: z.object({ q: z.string().min(1).max(200) }), response: NameSearchResult }),
  /** Melrys-only check of a dénomination / nom commercial while typing (the client's own dossier never counts). */
  nameAvailability: endpoint({ id: "creation.nameAvailability", method: "GET", path: "/creation/name-availability", query: z.object({ q: z.string().min(1).max(200) }), response: NameAvailability }),
  sirenCheck: endpoint({ id: "creation.sirenCheck", method: "POST", path: "/creation/insee/siren-check", body: z.object({ siren: z.string().regex(/^\d{9}$/) }), response: SirenCheck }),
  addresses: endpoint({ id: "creation.addresses", method: "GET", path: "/geo/addresses", query: z.object({ q: z.string().min(1) }), response: z.object({ results: z.array(AddressSuggestion) }) }),
  communes: endpoint({ id: "creation.communes", method: "GET", path: "/geo/communes", query: z.object({ q: z.string().min(1) }), response: z.object({ results: z.array(CommuneSuggestion) }) }),
  domiciliataires: endpoint({ id: "creation.domiciliataires", method: "GET", path: "/creation/domiciliataires", query: z.object({ q: z.string().min(1) }), response: z.object({ results: z.array(Domiciliataire) }) }),
  clauses: endpoint({ id: "creation.clauses", method: "GET", path: "/creation/clauses", query: z.object({ q: z.string() }), response: z.object({ suggestions: z.array(ClauseSuggestion) }) }),

  callback: endpoint({ id: "creation.callback", method: "GET", path: "/creation/callback", response: z.object({ callback: Callback.nullable() }) }),
  /** « Parler à un conseiller » (Parcours interrompu, A4): opens the callback of the recorded stop answer; the team is notified. 409 without a stop. */
  requestCallback: endpoint({ id: "creation.requestCallback", method: "POST", path: "/creation/callback", response: z.object({ callback: Callback }) }),
  updateCallbackPhone: endpoint({
    id: "creation.updateCallbackPhone",
    method: "PATCH",
    path: "/creation/callback",
    body: z.object({ phone: z.string().regex(/^\+[1-9]\d{6,14}$/, "Numéro de téléphone invalide.") }),
    response: z.object({ callback: Callback }),
  }),

  /** Price list, readable by any signed-in user (with or without a dossier). Static per release; cache it. */
  pricing: endpoint({ id: "creation.pricing", method: "GET", path: "/creation/pricing", response: CreationPricing }),
  advisorSlots: endpoint({ id: "creation.advisorSlots", method: "GET", path: "/advisor/slots", response: AdvisorSlots }),
  bookCall: endpoint({
    id: "creation.bookCall",
    method: "POST",
    path: "/advisor/appointments",
    body: z.object({ start: IsoDateTime, mode: CallMode, phone: z.string().nullable() }),
    response: z.object({ appointment: Appointment }),
  }),
};
